import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Search, Users, UserCheck, UserPlus, Clock, CheckCircle, XCircle,
  MapPin, Phone, Mail, Loader2, Building2, RefreshCw, Stethoscope
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function VendorLinkDoctors({ token, onRefresh }) {
  const [subTab, setSubTab] = useState('search');
  const [doctors, setDoctors] = useState([]);
  const [incomingRequests, setIncomingRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [specialtyFilter, setSpecialtyFilter] = useState('all');
  const [areaFilter, setAreaFilter] = useState('');
  const [requesting, setRequesting] = useState(null);
  const [processing, setProcessing] = useState(null);
  const getHeaders = () => ({ Authorization: `Bearer ${token || localStorage.getItem('vendor_token')}` });

  const fetchAvailableDoctors = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (searchText) params.search = searchText;
      if (specialtyFilter && specialtyFilter !== 'all') params.specialty = specialtyFilter;
      if (areaFilter) params.area = areaFilter;
      const res = await axios.get(`${API}/api/vendor-doctor-mappings/vendor/available-doctors`, { headers: getHeaders(), params });
      setDoctors(res.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [searchText, specialtyFilter, areaFilter, token]);

  const fetchIncomingRequests = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/vendor-doctor-mappings/vendor/incoming-requests`, { headers: getHeaders() });
      setIncomingRequests(res.data || []);
    } catch (err) { console.error(err); }
  }, [token]);

  useEffect(() => {
    fetchAvailableDoctors();
    fetchIncomingRequests();
  }, []);

  const handleSearch = () => {
    fetchAvailableDoctors();
  };

  const handleRequestConnect = async (doctorId) => {
    setRequesting(doctorId);
    try {
      await axios.post(`${API}/api/vendor-doctor-mappings/vendor/request-doctor`, { target_id: doctorId, notes: '' }, { headers: getHeaders() });
      toast.success('Connection request sent to doctor');
      fetchAvailableDoctors();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send request');
    } finally {
      setRequesting(null);
    }
  };

  const handleAccept = async (mappingId) => {
    setProcessing(mappingId);
    try {
      await axios.put(`${API}/api/vendor-doctor-mappings/vendor/accept/${mappingId}`, {}, { headers: getHeaders() });
      toast.success('Connection approved');
      fetchIncomingRequests();
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error('Failed to accept');
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (mappingId) => {
    setProcessing(mappingId);
    try {
      await axios.put(`${API}/api/vendor-doctor-mappings/vendor/reject/${mappingId}`, { reason: '' }, { headers: getHeaders() });
      toast.success('Connection declined');
      fetchIncomingRequests();
    } catch (err) {
      toast.error('Failed to reject');
    } finally {
      setProcessing(null);
    }
  };

  const getStatusBadge = (status) => {
    const map = {
      approved: { label: 'Connected', className: 'bg-green-100 text-green-700' },
      pending: { label: 'Pending', className: 'bg-yellow-100 text-yellow-700' },
      rejected: { label: 'Rejected', className: 'bg-red-100 text-red-700' },
      not_connected: { label: 'Not Connected', className: 'bg-slate-100 text-slate-500' }
    };
    const m = map[status] || { label: status, className: 'bg-slate-100 text-slate-500' };
    return <Badge className={m.className}>{m.label}</Badge>;
  };

  return (
    <div data-testid="vendor-link-doctors">
      <Tabs value={subTab} onValueChange={setSubTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="search" className="gap-1"><Search className="w-3.5 h-3.5" /> Find Doctors</TabsTrigger>
          <TabsTrigger value="incoming" className="gap-1">
            <UserPlus className="w-3.5 h-3.5" /> Incoming Requests
            {incomingRequests.length > 0 && <Badge className="ml-1 bg-red-500 text-white text-[10px] px-1.5">{incomingRequests.length}</Badge>}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="search">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Find & Connect with Doctors</CardTitle>
              <CardDescription>Search by name, phone, email, clinic, area, or specialty</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Search Filters */}
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                <div className="relative sm:col-span-2">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    placeholder="Name, phone, email, clinic..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    className="pl-9 text-sm"
                    data-testid="vendor-search-doctors-input"
                  />
                </div>
                <Select value={specialtyFilter} onValueChange={setSpecialtyFilter}>
                  <SelectTrigger data-testid="vendor-specialty-filter">
                    <SelectValue placeholder="Specialty" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Specialties</SelectItem>
                    <SelectItem value="general">General</SelectItem>
                    <SelectItem value="dentist">Dentist</SelectItem>
                    <SelectItem value="cardiology">Cardiology</SelectItem>
                    <SelectItem value="orthopedics">Orthopedics</SelectItem>
                    <SelectItem value="pediatrics">Pediatrics</SelectItem>
                    <SelectItem value="dermatology">Dermatology</SelectItem>
                    <SelectItem value="ophthalmology">Ophthalmology</SelectItem>
                    <SelectItem value="ent">ENT</SelectItem>
                    <SelectItem value="gynecology">Gynecology</SelectItem>
                    <SelectItem value="neurology">Neurology</SelectItem>
                  </SelectContent>
                </Select>
                <div className="flex gap-2">
                  <Input
                    placeholder="Area / City"
                    value={areaFilter}
                    onChange={(e) => setAreaFilter(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    className="text-sm"
                    data-testid="vendor-area-filter"
                  />
                  <Button size="sm" onClick={handleSearch}>
                    <Search className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Results */}
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-5 h-5 animate-spin text-teal-600" />
                </div>
              ) : doctors.length === 0 ? (
                <div className="text-center py-8 text-slate-400">No doctors found matching your filters</div>
              ) : (
                <div className="space-y-2">
                  {doctors.map((doc) => (
                    <div key={doc.id} className="flex items-center justify-between p-3 border rounded-lg bg-white hover:bg-slate-50 transition" data-testid="doctor-search-result">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <Stethoscope className="w-5 h-5 text-blue-600" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{doc.name}</p>
                          {doc.clinic_name && <p className="text-xs text-slate-500 flex items-center gap-1"><Building2 className="w-3 h-3" />{doc.clinic_name}</p>}
                          <div className="flex flex-wrap gap-2 mt-0.5">
                            {doc.specialty && <span className="text-[10px] text-violet-600 bg-violet-50 px-1.5 rounded">{doc.specialty}</span>}
                            {doc.phone && <span className="text-[10px] text-slate-400 flex items-center gap-0.5"><Phone className="w-2.5 h-2.5" />{doc.phone}</span>}
                            {doc.email && <span className="text-[10px] text-slate-400 flex items-center gap-0.5"><Mail className="w-2.5 h-2.5" />{doc.email}</span>}
                          </div>
                          {doc.address && <p className="text-[10px] text-slate-400 flex items-center gap-0.5 mt-0.5"><MapPin className="w-2.5 h-2.5" />{doc.address}</p>}
                        </div>
                      </div>
                      <div className="flex-shrink-0 ml-2">
                        {doc.connection_status === 'not_connected' ? (
                          <Button size="sm" onClick={() => handleRequestConnect(doc.id)} disabled={requesting === doc.id} data-testid="connect-doctor-btn">
                            {requesting === doc.id ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <UserPlus className="w-3 h-3 mr-1" />}
                            Connect
                          </Button>
                        ) : (
                          getStatusBadge(doc.connection_status)
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="incoming">
          <Card>
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-base">Incoming Doctor Requests</CardTitle>
                <CardDescription>Doctors who want to connect with you</CardDescription>
              </div>
              <Button size="sm" variant="outline" onClick={fetchIncomingRequests}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent>
              {incomingRequests.length === 0 ? (
                <p className="text-center py-8 text-slate-400">No pending requests</p>
              ) : (
                <div className="space-y-2">
                  {incomingRequests.map((req) => (
                    <div key={req.id} className="flex items-center justify-between p-3 border rounded-lg bg-yellow-50/50" data-testid="incoming-doctor-request">
                      <div className="min-w-0">
                        <p className="text-sm font-medium">Dr. {req.doctor_name || 'Unknown'}</p>
                        {req.clinic_name && <p className="text-xs text-slate-500">{req.clinic_name}</p>}
                        {req.doctor_specialty && <span className="text-[10px] text-violet-600 bg-violet-50 px-1.5 rounded">{req.doctor_specialty}</span>}
                        <div className="flex flex-wrap gap-2 mt-0.5">
                          {req.doctor_phone && <span className="text-[10px] text-slate-400"><Phone className="w-2.5 h-2.5 inline mr-0.5" />{req.doctor_phone}</span>}
                          {req.doctor_email && <span className="text-[10px] text-slate-400"><Mail className="w-2.5 h-2.5 inline mr-0.5" />{req.doctor_email}</span>}
                        </div>
                        {req.doctor_address && <p className="text-[10px] text-slate-400"><MapPin className="w-2.5 h-2.5 inline mr-0.5" />{req.doctor_address}</p>}
                      </div>
                      <div className="flex gap-2 flex-shrink-0">
                        <Button size="sm" onClick={() => handleAccept(req.id)} disabled={processing === req.id} className="bg-green-600 hover:bg-green-700" data-testid="accept-doctor-request-btn">
                          {processing === req.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3 mr-1" />}
                          Accept
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleReject(req.id)} disabled={processing === req.id} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="reject-doctor-request-btn">
                          <XCircle className="w-3 h-3 mr-1" /> Decline
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
